import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DataTable, DropButton, Button as GrommetButton, Layer, Paragraph, TextInput, Tip } from 'grommet';
import {
  CircleAlert,
  Close,
  Cycle,
  Document,
  Download,
  Edit,
  Filter,
  FormDown,
  FormUp,
  Save,
  StatusWarning,
  Trash,
  Upload,
} from 'grommet-icons';
import { UploadDocumentModal } from '../UploadDocumentModal';
import { ApiError } from '/src/api';
import { ClientFileService } from '/src/api/services/ClientFileService';
import { ClientFileTypeService } from '/src/api/services/ClientFileTypeService';
import {
  AddButton,
  Box,
  Button,
  Card,
  CardBody,
  DataTableHeader,
  FiltersDropdown,
  FormCard,
  InfoBox,
  LoadingSpinner,
  SearchInput,
  Text,
} from '/src/components';
import { config } from '/src/config';
import { ClientFile } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { pxToRem } from '/src/utils';

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface EditableDocument extends ClientFile {
  isEditing?: boolean;
}

type DocumentFilters = {
  typeId: string;
  uploadedById: string;
};

const DOCUMENT_FILTERS_DEFAULTS: DocumentFilters = { typeId: '', uploadedById: '' };

const REFRESH_INTERVAL_MS = config.documents.refreshIntervalMs;
const REFRESH_MAX_INTERVAL_MS = 30_000;
const REFRESH_TIMEOUT_MS = 5 * 60_000;

const CommentPreview = ({ comment }: { comment: string }) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (isExpanded || !element) return;
    const measure = () => setIsClamped(element.scrollHeight > element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [comment, isExpanded]);

  return (
    <Box
      direction="row"
      align={isExpanded ? 'start' : 'center'}
      gap="xsmall"
      height={{ min: '36px' }}
      style={{ width: 0, minWidth: '100%' }}
    >
      <Paragraph
        ref={textRef}
        size="small"
        fill
        margin="none"
        maxLines={isExpanded ? undefined : 3}
        style={{ fontFamily: 'Lato, sans-serif', flex: 1, minWidth: 0, wordBreak: 'break-word' }}
      >
        {comment}
      </Paragraph>
      {(isClamped || isExpanded) && (
        <Button
          icon={isExpanded ? <FormUp size="small" /> : <FormDown size="small" />}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          tip={isExpanded ? t('documents:collapse') : t('documents:seeMore')}
        />
      )}
    </Box>
  );
};

const CommentEditor = ({ initialValue, onChange }: { initialValue: string; onChange: (value: string) => void }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialValue);

  return (
    <Box direction="row" align="center" height="36px">
      <TextInput
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          onChange(e.target.value);
        }}
        maxLength={500}
      />
      <Text size="small" color="dark-6" margin={{ left: 'small' }} wordBreak="break-word">
        {draft.length + t('documents:characterLimit')}
      </Text>
    </Box>
  );
};

export const DocumentLibrary = ({ clientId }: { clientId: number }) => {
  /** Config **/
  const { t } = useTranslation();
  const tooltips = tooltipMessages();

  /** State **/
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documents, setDocuments] = useState<EditableDocument[]>([]);
  const [isDeletingId, setIsDeletingId] = useState<number | null>(null);
  const [isSavingId, setIsSavingId] = useState<number | null>(null);
  const [isDownloadingId, setIsDownloadingId] = useState<number | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<EditableDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<DocumentFilters>(DOCUMENT_FILTERS_DEFAULTS);
  const [draftFilters, setDraftFilters] = useState<DocumentFilters>(DOCUMENT_FILTERS_DEFAULTS);
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  const [isFiltersMenuHovered, setIsFiltersMenuHovered] = useState(false);
  const commentDrafts = useRef(new Map<number, string>());
  const hasActiveFilters = !!(appliedFilters.typeId || appliedFilters.uploadedById);

  /** Computed **/
  const buildOptions = useCallback(
    (entries: { id: number; name: string }[]) => {
      const unique = new Map(entries.map((entry) => [entry.id, { label: entry.name, value: String(entry.id) }]));
      return [
        { label: t('documents:all'), value: '' },
        ...Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label)),
      ];
    },
    [t]
  );

  const typeOptions = useMemo(
    () =>
      buildOptions(
        documents.flatMap((doc) =>
          doc.client_file_type?.id && doc.client_file_type?.name
            ? [{ id: doc.client_file_type.id, name: doc.client_file_type.name }]
            : []
        )
      ),
    [documents, buildOptions]
  );

  const uploadedByOptions = useMemo(
    () =>
      buildOptions(
        documents.flatMap((doc) => (doc.user?.id && doc.user?.name ? [{ id: doc.user.id, name: doc.user.name }] : []))
      ),
    [documents, buildOptions]
  );

  const filteredDocuments = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return documents.filter((doc) => {
      if (normalizedQuery && !doc.name?.toLowerCase().includes(normalizedQuery)) {
        return false;
      }
      if (appliedFilters.typeId && String(doc.client_file_type?.id) !== appliedFilters.typeId) {
        return false;
      }
      if (appliedFilters.uploadedById && String(doc.user?.id) !== appliedFilters.uploadedById) {
        return false;
      }
      return true;
    });
  }, [documents, searchQuery, appliedFilters]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const visibleIds = new Set(filteredDocuments.map((doc) => doc.id));
      const pruned = prev.filter((id) => visibleIds.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [filteredDocuments]);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      const clientFilesResponse = await ClientFileService.listClientFiles(clientId.toString(), {
        limit: 2000,
      });

      await ClientFileTypeService.listClientFileTypes();

      const filteredDocs = clientFilesResponse.data.filter((doc) => doc.client_id === clientId);
      setDocuments(filteredDocs);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(toastMessages.fetchDocuments.error(error as ApiError));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [clientId]);

  const processingKey = useMemo(
    () =>
      documents
        .filter((doc) => doc.client_file_state?.id === 1)
        .map((doc) => doc.id)
        .join(','),
    [documents]
  );

  useEffect(() => {
    if (!processingKey) return;

    const processingDocs = processingKey.split(',').map(Number);
    const startedAt = Date.now();
    let delay = REFRESH_INTERVAL_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const schedule = () => {
      if (Date.now() - startedAt >= REFRESH_TIMEOUT_MS) return;
      clearTimeout(timer);
      timer = setTimeout(refreshDocuments, delay);
      delay = Math.min(delay * 2, REFRESH_MAX_INTERVAL_MS);
    };

    const refreshDocuments = async () => {
      const results = await Promise.allSettled(processingDocs.map((id) => ClientFileService.getClientFile(id)));
      if (cancelled) return;

      const updates = new Map<number, ClientFile>();
      results.forEach((result) => {
        if (result.status === 'fulfilled') updates.set(result.value.id, result.value);
        else console.error('Error refreshing documents:', result.reason);
      });

      setDocuments((prevDocs) => {
        let changed = false;
        const nextDocs = prevDocs.map((doc) => {
          const updated = updates.get(doc.id);
          if (!updated || updated.client_file_state_id === doc.client_file_state_id) return doc;
          changed = true;
          return {
            ...doc,
            client_file_state_id: updated.client_file_state_id,
            client_file_state: updated.client_file_state,
          };
        });
        return changed ? nextDocs : prevDocs;
      });
      schedule();
    };

    schedule();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [processingKey]);

  /** Methods **/
  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const applyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
    setIsFiltersMenuOpen(false);
  }, [draftFilters]);

  const clearFilters = useCallback(() => {
    setDraftFilters(DOCUMENT_FILTERS_DEFAULTS);
    setAppliedFilters(DOCUMENT_FILTERS_DEFAULTS);
    setIsFiltersMenuOpen(false);
  }, []);

  const openFilters = useCallback(() => {
    setDraftFilters(appliedFilters);
    setIsFiltersMenuOpen(true);
  }, [appliedFilters]);

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    const documentId = documentToDelete.id;
    try {
      setIsDeletingId(documentId);
      await ClientFileService.deleteClientFile(documentId);

      setDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== documentId));
      toast.success(toastMessages.deleteDocument.success);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(toastMessages.deleteDocument.error(error as ApiError));
    } finally {
      setIsDeletingId(null);
    }
  };

  const handleBulkDownload = useCallback(async () => {
    const selectedDocs = documents.filter((doc) => selectedIds.includes(doc.id));
    const docsToDownload = selectedDocs.filter(
      (doc) => doc.client_file_state?.id !== 1 && doc.client_file_state?.id !== 2
    );
    const skippedCount = selectedDocs.length - docsToDownload.length;
    if (!docsToDownload.length) {
      toast.error(t('documents:bulkDownloadUnavailable', { count: selectedDocs.length }));
      return;
    }

    try {
      setIsBulkDownloading(true);

      for (const doc of docsToDownload) {
        const { url } = await ClientFileService.getClientFileUrl(doc.id);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      toast.success(
        skippedCount > 0
          ? t('documents:bulkDownloadPartial', {
              downloaded: docsToDownload.length,
              skipped: skippedCount,
            })
          : toastMessages.bulkDownloadDocuments.success
      );
      setSelectedIds([]);
    } catch (error) {
      console.error('Error bulk downloading documents:', error);
      toast.error(toastMessages.bulkDownloadDocuments.error(error as ApiError));
    } finally {
      setIsBulkDownloading(false);
    }
  }, [documents, selectedIds, t]);

  const handleDownloadDocument = async (documentFile: EditableDocument) => {
    try {
      setIsDownloadingId(documentFile.id);
      const response = await ClientFileService.getClientFileUrl(documentFile.id);

      const link = document.createElement('a');
      link.href = response.url;
      link.download = documentFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(toastMessages.downloadDocument.success);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error(toastMessages.downloadDocument.error(error as ApiError));
    } finally {
      setIsDownloadingId(null);
    }
  };

  const handleEditDocument = (document: EditableDocument) => {
    commentDrafts.current.set(document.id, document.comments ?? '');
    setDocuments((prevDocs) => prevDocs.map((doc) => (doc.id === document.id ? { ...doc, isEditing: true } : doc)));
  };

  const handleCancelEdit = (documentId: number) => {
    commentDrafts.current.delete(documentId);
    setDocuments((prevDocs) => prevDocs.map((doc) => (doc.id === documentId ? { ...doc, isEditing: false } : doc)));
  };

  const handleSaveDocument = async (document: EditableDocument) => {
    const comments = commentDrafts.current.get(document.id)?.trim() || null;
    try {
      setIsSavingId(document.id);

      await ClientFileService.updateClientFile(document.id, { comments });

      commentDrafts.current.delete(document.id);
      setDocuments((prevDocs) =>
        prevDocs.map((doc) => (doc.id === document.id ? { ...doc, comments, isEditing: false } : doc))
      );

      toast.success(toastMessages.updateDocument.success);
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error(toastMessages.updateDocument.error(error as ApiError));
    } finally {
      setIsSavingId(null);
    }
  };

  const handleFileUpload = async (file: File, typeId: number, comments: string | null) => {
    try {
      setIsLoading(true);
      const uploadedFile = await ClientFileService.createClientFile(clientId.toString(), {
        file,
        client_file_type_id: typeId,
        comments: comments,
      });

      const response = await ClientFileService.listClientFiles(clientId.toString(), {
        limit: 2000,
      });

      const filteredDocs = response.data.filter((doc) => doc.client_id === clientId);
      const newDocExists = filteredDocs.some((doc) => doc.name === file.name && doc.client_id === clientId);

      if (!newDocExists && uploadedFile) {
        filteredDocs.push(uploadedFile);
      }

      setDocuments(filteredDocs);
      toast.success(toastMessages.uploadDocument.success);
      setShowUploadModal(false);
    } catch (error: unknown) {
      console.error('DocumentLibrary: File upload failed:', error);
      toast.error(toastMessages.uploadDocument.error(error as ApiError));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDeleteModal = (document: EditableDocument) => {
    setDocumentToDelete(document);
  };

  const handleCloseDeleteModal = () => {
    setDocumentToDelete(null);
  };

  /** Render **/
  return (
    <Box>
      <Box direction="row" align="center" justify="between" pad={{ bottom: '1rem' }}>
        <Text color="dark-1" size="large" weight={300}>
          {t('documents:title')}
        </Text>

        <Box direction="row" align="center" gap="small">
          <SearchInput
            searchQuery={searchQuery}
            placeholder={t('documents:searchByName')}
            setSearchQuery={setSearchQuery}
            onSearch={setSearchQuery}
          />
          <DropButton
            plain
            icon={<Filter color={isFiltersMenuHovered || isFiltersMenuOpen ? 'brand' : undefined} />}
            open={isFiltersMenuOpen}
            onOpen={openFilters}
            onClose={() => setIsFiltersMenuOpen(false)}
            onMouseOver={() => setIsFiltersMenuHovered(true)}
            onMouseOut={() => setIsFiltersMenuHovered(false)}
            tip={tooltips.filterDocuments}
            dropAlign={{ right: 'right', top: 'bottom' }}
            dropContent={
              <FiltersDropdown
                isFiltering={false}
                onSubmit={applyFilters}
                onClear={clearFilters}
                filters={[
                  {
                    label: t('documents:documentType'),
                    value: draftFilters.typeId,
                    setValue: (value) => setDraftFilters({ ...draftFilters, typeId: (value as string) || '' }),
                    options: typeOptions,
                  },
                  {
                    label: t('documents:uploadedBy'),
                    value: draftFilters.uploadedById,
                    setValue: (value) => setDraftFilters({ ...draftFilters, uploadedById: (value as string) || '' }),
                    options: uploadedByOptions,
                  },
                ]}
              />
            }
          />
          {selectedIds.length > 0 && (
            <AddButton
              label={`${isBulkDownloading ? t('documents:preparingDownload') : t('documents:downloadSelected')} (${
                selectedIds.length
              })`}
              onClick={handleBulkDownload}
              disabled={isBulkDownloading}
              icon={<Download size="small" />}
            />
          )}
          <AddButton label={t('documents:uploadDocument')} onClick={handleUploadClick} icon={<Upload size="small" />} />
        </Box>
      </Box>

      {showUploadModal && <UploadDocumentModal onClose={() => setShowUploadModal(false)} onUpload={handleFileUpload} />}
      {documentToDelete && (
        <Layer
          position="center"
          onEsc={handleCloseDeleteModal}
          onClickOutside={handleCloseDeleteModal}
          responsive={false}
          style={{
            width: 'auto',
            maxWidth: 'min(95vw, 500px)',
            maxHeight: '95vh',
            overflow: 'auto',
            borderRadius: '8px',
          }}
        >
          <FormCard
            width="100%"
            title={t('documents:deleteDocument')}
            icon={<Document size="20px" color="status-warning" />}
            onSubmit={handleDeleteDocument}
            isLoading={isDeletingId === documentToDelete.id}
            hideRequiredText
            saveButtonLabel="DELETE"
            saveButtonLoadingLabel="DELETING..."
            saveButtonDisabled={isDeletingId === documentToDelete.id}
            headerChildren={[
              <GrommetButton
                key="close"
                pad="none"
                onClick={handleCloseDeleteModal}
                icon={<Close size="20px" />}
                tip={t('common:close')}
              />,
            ]}
          >
            <Box pad={{ horizontal: 'medium' }} gap="small">
              <InfoBox gap="1rem" direction="row" align="center">
                <StatusWarning size="medium" color="status-warning" />
                <Text>
                  {t('documents:deleteDocumentWarning')}
                  <br />
                  <Text weight="bold" as="span" wordBreak="break-word">
                    {documentToDelete.name}
                  </Text>
                </Text>
              </InfoBox>
            </Box>
          </FormCard>
        </Layer>
      )}

      <Card>
        <CardBody pad="none" gap="none">
          {isLoading && <LoadingSpinner />}
          {!isLoading && (
            <Box elevation="small">
              <DataTable
                pad={{ horizontal: '1rem' }}
                columns={[
                  {
                    property: 'name',
                    size: '39%',
                    header: <DataTableHeader title={t('documents:documentName')} />,
                    render: (document: EditableDocument) => (
                      <Box margin={{ vertical: pxToRem('14px') }} direction="row" align="center" gap="xsmall">
                        {document.client_file_state?.id === 1 && (
                          <Tip content={t('documents:processing')}>
                            <Cycle className="rotate" color="accent-1" size="18px" />
                          </Tip>
                        )}
                        {document.client_file_state?.id === 2 && (
                          <Tip content={t('documents:needsReview')}>
                            <CircleAlert color="status-warning" size="18px" />
                          </Tip>
                        )}
                        <Text
                          size="medium"
                          fontFamily="Lato, sans-serif"
                          wordBreak="break-word"
                          style={{ textWrap: 'pretty' }}
                        >
                          {document.name}
                        </Text>
                      </Box>
                    ),
                  },
                  {
                    property: 'client_file_type.name',
                    size: '16%',
                    header: <DataTableHeader title={t('documents:documentType')} />,
                    render: (document: EditableDocument) => (
                      <Box margin={{ vertical: pxToRem('14px') }}>
                        <Text size="medium" fontFamily="Lato, sans-serif">
                          {document.client_file_type?.name || 'Unknown'}
                        </Text>
                      </Box>
                    ),
                  },
                  {
                    property: 'uploaded_at',
                    header: <DataTableHeader title={t('documents:dateUploaded')} style={{ whiteSpace: 'nowrap' }} />,
                    render: (document: EditableDocument) => (
                      <Box margin={{ vertical: pxToRem('14px') }}>
                        <Text size="medium" fontFamily="Lato, sans-serif" style={{ whiteSpace: 'nowrap' }}>
                          {document.uploaded_at ? formatDate(document.uploaded_at) : 'N/A'}
                        </Text>
                      </Box>
                    ),
                  },
                  {
                    property: 'user.name',
                    size: '15%',
                    header: <DataTableHeader title={t('documents:uploadedBy')} />,
                    render: (document: EditableDocument) => (
                      <Box margin={{ vertical: pxToRem('14px') }}>
                        <Text size="medium" fontFamily="Lato, sans-serif">
                          {document.user?.name || 'N/A'}
                        </Text>
                      </Box>
                    ),
                  },
                  {
                    property: 'comments',
                    size: '30%',
                    header: <DataTableHeader title={t('common:comments')} />,
                    render: (document: EditableDocument) => (
                      <Box margin={{ vertical: pxToRem('14px') }}>
                        {document.isEditing ? (
                          <CommentEditor
                            initialValue={commentDrafts.current.get(document.id) ?? ''}
                            onChange={(value) => commentDrafts.current.set(document.id, value)}
                          />
                        ) : (
                          <CommentPreview comment={document.comments || t('documents:noComments')} />
                        )}
                      </Box>
                    ),
                  },
                  {
                    property: 'actions',
                    sortable: false,
                    header: <DataTableHeader title="" />,
                    render: (document: EditableDocument) => (
                      <Box
                        margin={{ vertical: pxToRem('18px') }}
                        direction="row"
                        gap="small"
                        justify="center"
                        width={'175px'}
                      >
                        {document.isEditing ? (
                          <>
                            <Box direction="row" gap="small" justify="center" key={`edit-${document.id}`}>
                              <Button
                                icon={<Save size="small" />}
                                onClick={() => handleSaveDocument(document)}
                                disabled={isSavingId === document.id}
                                tip={t('documents:saveChanges')}
                              />
                              <Button
                                icon={<Close size="small" />}
                                onClick={() => handleCancelEdit(document.id)}
                                disabled={isSavingId === document.id}
                                tip={t('documents:cancelEditing')}
                              />
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box direction="row" gap="small" justify="center" key={`view-${document.id}`}>
                              <Tip
                                content={
                                  document.client_file_state?.id === 1
                                    ? t('documents:cannotDownloadWhileProcessing')
                                    : document.client_file_state?.id === 2
                                      ? t('documents:cannotDownloadFileNeedsReview')
                                      : t('documents:downloadDocument')
                                }
                              >
                                <span style={{ display: 'inline-block', pointerEvents: 'auto' }}>
                                  <Button
                                    icon={<Download size="small" />}
                                    onClick={() => handleDownloadDocument(document)}
                                    disabled={
                                      isDownloadingId === document.id ||
                                      document.client_file_state?.id === 2 ||
                                      document.client_file_state?.id === 1
                                    }
                                    style={{ pointerEvents: 'auto' }}
                                  />
                                </span>
                              </Tip>
                              <Button
                                icon={<Edit size="small" />}
                                onClick={() => handleEditDocument(document)}
                                tip={t('documents:editComments')}
                              />
                              <Button
                                icon={<Trash size="small" />}
                                onClick={() => handleOpenDeleteModal(document)}
                                disabled={isDeletingId === document.id}
                                tip={t('documents:deleteDocument')}
                              />
                            </Box>
                          </>
                        )}
                      </Box>
                    ),
                  },
                ]}
                data={filteredDocuments}
                primaryKey="id"
                select={selectedIds}
                onSelect={(ids) => setSelectedIds(ids.map(Number))}
                sortable
                step={20}
                paginate={{ alignSelf: 'center', size: 'small', margin: { vertical: '1.5rem' } }}
                background={['light-6', 'white']}
                style={{ minWidth: '900px' }}
              />
            </Box>
          )}
          {filteredDocuments.length === 0 && !isLoading && (
            <Box
              pad={{ horizontal: '1.5rem', vertical: '2rem' }}
              background="light-6"
              align="center"
              justify="center"
              gap="small"
            >
              <Text size="medium" fontFamily="Lato, sans-serif">
                {hasActiveFilters ? t('documents:noDocumentsMatchFilters') : t('documents:noDocumentsFound')}
              </Text>
              {hasActiveFilters && <Button label={t('documents:clearFilters')} onClick={clearFilters} />}
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};
